import { createJSONStorage } from "@/storage/browser"
import { readUserSessionToken } from "@/storage/user-session"
import type { ModelPreferences } from "@/transport/preferences"

// Disposable cache of the account's model preferences, so a returning device
// opens chats with the models and settings the account last had while the
// control plane answers. Edits still wait for a loaded copy.
const storage = createJSONStorage<{ scope: string; preferences: ModelPreferences } | undefined>(
  "mux.model-preferences.snapshot.v1",
  {
    decode(value) {
      if (!value || typeof value !== "object") return undefined
      const candidate = value as { scope?: unknown; preferences?: Partial<Record<keyof ModelPreferences, unknown>> }
      const preferences = candidate.preferences
      if (typeof candidate.scope !== "string" || !preferences || typeof preferences !== "object") return undefined
      const lists = [preferences.user, preferences.recent, preferences.contextWindows, preferences.variants, preferences.serviceTiers, preferences.responseStyles]
      if (!lists.every(Array.isArray) || !preferences.providerTitles || typeof preferences.providerTitles !== "object") return undefined
      return value as { scope: string; preferences: ModelPreferences }
    },
  },
)

export function readModelPreferencesSnapshot() {
  const scope = readUserSessionToken()
  const value = storage.read(undefined)
  return scope && value?.scope === scope ? value.preferences : undefined
}

export function seedModelPreferencesSnapshot(preferences: ModelPreferences) {
  const scope = readUserSessionToken()
  if (scope) storage.write({ scope, preferences })
}

// The snapshot's scope is the session token, so it must go with the session.
export function clearModelPreferencesSnapshot() {
  storage.remove()
}
